import React, { useRef, useEffect } from "react";
import { Button, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import QRCodeStyling from "qr-code-styling";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import InprofileLogo from "Assets/Logo/logo.png";
import GoogleLogo from "Assets/google-logo.png";

const QRCode = ({ userName, businessName, showQRCode }) => {
  const qrRef = useRef(null);
  const flyerRef = useRef(null);
  const qrCodeRef = useRef(null);

  // Initialize QRCode instance once
  useEffect(() => {
    if (userName && !qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling({
        width: 260,
        height: 260,
        type: "svg",
        data: `https://inprofile.co/${userName}/review`,
        margin: 0,
        dotsOptions: {
          color: "#000000",
          type: "rounded",
        },
        cornersSquareOptions: {
          color: "#000000",
          type: "extra-rounded",
        },
        cornersDotOptions: {
          color: "#000000",
          type: "dot",
        },
        backgroundOptions: {
          color: "#ffffff",
        },
        imageOptions: {
          hideBackgroundDots: false,
          margin: 0,
          imageSize: 0,
        },
        qrOptions: {
          typeNumber: 0,
          mode: "Byte",
          errorCorrectionLevel: "M",
        },
      });
    }
  }, [userName]);

  // Append / update QR code when container is ready
  useEffect(() => {
    if (qrCodeRef.current && qrRef.current && showQRCode) {
      // clear old content
      qrRef.current.innerHTML = "";

      // append fresh QR
      qrCodeRef.current.append(qrRef.current);

      // update data if userName changes
      qrCodeRef.current.update({
        data: `https://inprofile.co/${userName}/review`,
      });
    }
  }, [userName, showQRCode]);

  const downloadPng = async () => {
    if (!flyerRef.current) return;
    try {
      const dataUrl = await toPng(flyerRef.current, {
        pixelRatio: 5, // Increased quality
        cacheBust: true,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `${userName}-review-qr.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      message.error("Failed to download PNG");
    }
  };

  const downloadPdf = async () => {
    if (!flyerRef.current) return;
    try {
      const dataUrl = await toPng(flyerRef.current, {
        pixelRatio: 5,
        cacheBust: true,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // mm
      const usableWidth = pageWidth - margin * 2;
      // Compute image height maintaining aspect ratio
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => (img.onload = res));
      const ratio = img.height / img.width;
      const imgWidth = usableWidth;
      const imgHeight = imgWidth * ratio;
      const y = (pageHeight - imgHeight) / 2; // center vertically
      pdf.addImage(
        dataUrl,
        "PNG",
        margin,
        Math.max(margin, y),
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      );
      pdf.save(`${userName}-review-qr.pdf`);
    } catch (e) {
      message.error("Failed to download PDF");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`https://inprofile.co/${userName}/review`);
    message.success("Link copied!");
  };

  const openReviewPage = () => {
    window.open(`https://inprofile.co/${userName}/review`, "_blank");
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-6 flex flex-col items-center justify-start w-full">
      <h2 className="text-lg font-bold mb-4 text-center">QR Code</h2>
      <div className="flex flex-col items-center w-full">
        {/* Flyer container for export */}
        <div
          ref={flyerRef}
          className="w-full max-w-[420px] bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
          {/* Top logo */}
          <div className="flex flex-col items-center pt-8 pb-3 sm:px-8 px-4">
            <div className="text-2xl font-semibold text-gray-900 mb-2 text-center">
              {businessName}
            </div>
            <div className="mt-1s text-base tracking-wide text-gray-600">
              AI ✨ POWERED
            </div>
            <div className="mt-2 text-base tracking-wide bg-gradient-to-r from-[#FBBC05] to-[#EA4335] text-white rounded-2xl px-5 py-1 font-semibold shadow-md">
              Google Review Assistant
            </div>
          </div>

          {/* Gradient bordered QR */}
          <div className="review-page-container sm:px-16 pb-6 text-center">
            {/* Colored corner frame */}
            <div className="p-[6px] squircle bg-[conic-gradient(at_50%_50%,#dc2626_0deg,#f59e0b_90deg,#16a34a_180deg,#2563eb_270deg,#dc2626_360deg)]">
              <div className="bg-white squircle flex items-center justify-center">
                <div ref={qrRef} className="rounded-md p-2"></div>
              </div>
            </div>

            {/* Review text */}
            <div className="mt-4 text-center text-base text-gray-700">
              Give us Review on
            </div>

            {/* Google wordmark */}
            <div className="mt-1 flex justify-center">
              <img
                src={GoogleLogo}
                alt="google-logo"
                className="h-8 object-contain"
              />
            </div>

            {/* Stars */}
            <div className="mt-1 flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  className="w-5 h-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" />
                </svg>
              ))}
            </div>
          </div>

          {/* Branding footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-center gap-2">
            <span className="text-gray-600 text-base">Powered by</span>
            <img src={InprofileLogo} alt="Inprofile" className="w-7 h-7" />
            <span className="text-gray-800 text-base font-semibold">
              inprofile.co
            </span>
          </div>
        </div>

        <p className="text-gray-600 mt-4 mb-3 text-center break-words">
          Your customers can scan this QR code to leave a review—no need to
          think about what to write.
        </p>

        <div className="flex flex-col justify-center gap-2 w-full max-w-[360px]">
          <div className="flex flex-col sm:flex-row justify-center gap-2 w-full">
            <Button
              onClick={downloadPng}
              className="rounded-lg h-10 font-medium w-full">
              Download PNG
            </Button>
            <Button
              onClick={downloadPdf}
              className="rounded-lg h-10 font-medium w-full">
              Download PDF
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-2 w-full">
            <Button
              onClick={openReviewPage}
              className="rounded-lg h-10 font-medium w-full">
              inprofile.co/{userName}/review
            </Button>
            <Button
              onClick={copyLink}
              className="rounded-lg h-10 font-medium w-full"
              icon={<FontAwesomeIcon icon={faCopy} />}>
              Copy Link
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCode;
